import { STORAGE_KEY } from '@/constants';
import { WORKSPACE_STORAGE_KEY } from '@/constants/workspace';
import { getLocalStorageWithoutParsing } from './local-storage';
import appLogger from './logger';

// Global AbortController for fetch requests (parallel to axios one)
// When switching workspace, we abort all in-flight fetch requests
let globalFetchAbortController: AbortController | null = null;

// Cancel all in-flight fetch requests (called when switching workspace)
export const cancelAllFetchRequests = () => {
  if (globalFetchAbortController) {
    appLogger.info('🚫 Cancelling all in-flight fetch requests due to workspace switch');
    globalFetchAbortController.abort();
    globalFetchAbortController = null;
  }
  // Create new controller for future requests
  globalFetchAbortController = new AbortController();
};

// Initialize on module load
globalFetchAbortController = new AbortController();

export const askIOTApiFetch = (url: string, body?: object, method?: string): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      // Get fresh token on each request to handle login/logout properly
      const access_token = getLocalStorageWithoutParsing(STORAGE_KEY.ACCESS_TOKEN);
      
      // CRITICAL FIX: Add workspace ID header for private API calls
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${access_token}`,
      };

      // Add workspace header for private API calls (same logic as axios)
      if (typeof window !== 'undefined' && url.includes('/private/')) {
        const workspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
        // Only add header if workspaceId exists and is not "null" or "undefined" string
        if (workspaceId && workspaceId !== 'null' && workspaceId !== 'undefined') {
          headers['x-workspace-id'] = workspaceId;
        }
      }

      // CRITICAL FIX: Add global abort signal to enable request cancellation
      const fetchOptions: RequestInit = {
        method: method ? method : body ? 'POST' : 'GET',
        ...(body ? { body: JSON.stringify(body) } : {}),
        headers,
        signal: globalFetchAbortController?.signal, // Enable cancellation on workspace switch
      };

      const response = await fetch(url, fetchOptions);

      if (response.status !== 200 && response?.status !== 201) {
        throw response;
      }
      const responseData = await response.json();
      resolve(responseData);
    } catch (error: any) {
      // Silently handle aborted requests (intentional cancellation during workspace switch)
      if (error?.name === 'AbortError') {
        appLogger.info('Fetch request cancelled (workspace switch)', { url });
        reject(error);
        return;
      }
      reject(error);
    }
  });
};
